import React, { useState, useEffect } from 'react';
import {
    User, Package, FileText, DollarSign, Calendar,
    Mail, Phone, MapPin, Download, Eye, X,
    CheckCircle, Clock, AlertCircle, LogOut,
    ChevronLeft, ChevronRight, Search, Filter
} from 'lucide-react';

export default function AdminPortal() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [stats, setStats] = useState({
        totalOrders: 0,
        paidOrders: 0,
        totalRevenue: 0,
        pendingOrders: 0,
        recentOrders: []
    });
    const [loginForm, setLoginForm] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const API_URL = 'https://api.cafpraticheonline.it/api/admin';

    // Check if already authenticated
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            setIsAuthenticated(true);
            loadDashboard();
        }
    }, []);

    // Login function
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginForm)
            });

            const data = await response.json();

            if (data.success) {
                const cookieConsent = localStorage.getItem('cookieConsent');

                if (cookieConsent === 'true') {
                    // ✅ User accepted cookies — safe to store token
                    localStorage.setItem('adminToken', data.token);
                } else {
                    // ⚠️ User declined cookies — store token temporarily in memory
                    sessionStorage.setItem('adminToken', data.token);
                }

                setIsAuthenticated(true);
                loadDashboard();
            } else {
                setError(data.message || 'Login failed');
            }

        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    // Logout function
    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        setLoginForm({ username: '', password: '' });
    };

    // Load dashboard data
    const loadDashboard = async () => {
        setLoading(true);
        const token = localStorage.getItem('adminToken');

        try {
            // Load statistics
            const statsResponse = await fetch(`${API_URL}/dashboard/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const statsData = await statsResponse.json();
            if (statsData.success) {
                setStats(statsData.stats);
            }

            // Load orders
            const ordersResponse = await fetch(`${API_URL}/orders`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const ordersData = await ordersResponse.json();
            if (ordersData.success) {
                setOrders(ordersData.orders);
            }
        } catch (err) {
            console.error('Error loading dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load single order details
    const loadOrderDetails = async (orderId) => {
        const token = localStorage.getItem('adminToken');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/orders/${orderId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                setSelectedOrder(data.order);
            }
        } catch (err) {
            console.error('Error loading order details:', err);
        } finally {
            setLoading(false);
        }
    };

    // Update order status
    const updateOrderStatus = async (orderId, newStatus) => {
        const token = localStorage.getItem('adminToken');

        try {
            const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            const data = await response.json();
            if (data.success) {
                loadDashboard();
                if (selectedOrder && selectedOrder.order_id === orderId) {
                    loadOrderDetails(orderId);
                }
            }
        } catch (err) {
            console.error('Error updating order status:', err);
        }
    };

    // Format date
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `€${parseFloat(amount).toFixed(2)}`;
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
            paid: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
            processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Package },
            completed: { bg: 'bg-purple-100', text: 'text-purple-800', icon: CheckCircle },
            cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: X }
        };

        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-3 h-3 mr-1" />
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    // Filter orders
    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.customer_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.order_id?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Login Form
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
                    <div className="text-center mb-8">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <User className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800">Admin Portal</h1>
                        <p className="text-gray-600 mt-2">Sign in to access the dashboard</p>
                    </div>

                    <form onSubmit={handleLogin}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    value={loginForm.username}
                                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter username"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter password"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50"
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>

                        <p className="text-center text-xs text-gray-500 mt-4">
                            Default credentials: admin / admin
                        </p>
                    </form>
                </div>
            </div>
        );
    }

    // Main Dashboard
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg mr-3">
                                <User className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                                <p className="text-sm text-gray-500">Manage orders and customers</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Navigation Tabs */}
            <div className="bg-white border-b px-4 sm:px-6 lg:px-8">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition ${activeTab === 'dashboard'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Dashboard
                    </button>
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition ${activeTab === 'orders'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Orders
                    </button>
                </nav>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'dashboard' && (
                    <div>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="bg-blue-100 rounded-lg p-3 mr-4">
                                        <Package className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Total Orders</p>
                                        <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="bg-green-100 rounded-lg p-3 mr-4">
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Paid Orders</p>
                                        <p className="text-2xl font-bold text-gray-900">{stats.paidOrders}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="bg-yellow-100 rounded-lg p-3 mr-4">
                                        <Clock className="w-6 h-6 text-yellow-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Pending</p>
                                        <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="bg-purple-100 rounded-lg p-3 mr-4">
                                        <DollarSign className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Total Revenue</p>
                                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Orders */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b">
                                <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {stats.recentOrders.map(order => (
                                            <tr key={order.order_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {order.order_id.slice(0, 8)}...
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {order.customer_first_name} {order.customer_last_name}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {order.service_name}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {formatCurrency(order.amount)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {getStatusBadge(order.status)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {formatDate(order.created_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'orders' && (
                    <div>
                        {/* Filters */}
                        <div className="bg-white rounded-lg shadow p-4 mb-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="flex items-center flex-1">
                                    <Search className="w-5 h-5 text-gray-400 mr-2" />
                                    <input
                                        type="text"
                                        placeholder="Search orders..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-5 h-5 text-gray-400" />
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                        <option value="processing">Processing</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Orders Table */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredOrders.length > 0 ? (
                                            filteredOrders.map(order => (
                                                <tr key={order.order_id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 text-sm text-gray-900">{order.order_id.slice(0, 8)}...</td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">{order.customer_first_name} {order.customer_last_name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{order.customer_email}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">{order.service_name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(order.amount)}</td>
                                                    <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(order.created_at)}</td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button
                                                            onClick={() => loadOrderDetails(order.order_id)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            <Eye className="w-5 h-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                                    No orders found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Order Details Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center p-4 z-50">
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-xl font-bold text-gray-900">Order Details</h3>
                            <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-700">Order Information</h4>
                                    <p className="mt-2 text-sm text-gray-600"><span className="font-medium">Order ID:</span> {selectedOrder.order_id}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Status:</span> {getStatusBadge(selectedOrder.status)}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Amount:</span> {formatCurrency(selectedOrder.amount)}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Date:</span> {formatDate(selectedOrder.created_at)}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-700">Customer Information</h4>
                                    <p className="mt-2 text-sm text-gray-600"><span className="font-medium">Name:</span> {selectedOrder.customer_first_name} {selectedOrder.customer_last_name}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Email:</span> {selectedOrder.customer_email}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Phone:</span> {selectedOrder.customer_phone || 'N/A'}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Address:</span> {selectedOrder.customer_address}, {selectedOrder.customer_city}, {selectedOrder.customer_country}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-semibold text-gray-700">Service Details</h4>
                                <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-600"><span className="font-medium">Service:</span> {selectedOrder.service_name}</p>
                                    <p className="text-sm text-gray-600"><span className="font-medium">Description:</span> {selectedOrder.service_description}</p>
                                </div>
                            </div>

                            {selectedOrder.documents && selectedOrder.documents.length > 0 && (
                                <div>
                                    <h4 className="font-semibold text-gray-700">Documents ({selectedOrder.documents.length})</h4>
                                    <div className="mt-2 space-y-2">
                                        {selectedOrder.documents.map(doc => (
                                            <div key={doc.document_id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                                                <div className="flex items-center">
                                                    <FileText className="w-5 h-5 text-gray-400 mr-2" />
                                                    <span className="text-sm text-gray-800">{doc.file_name}</span>
                                                </div>
                                                <a
                                                    href={doc.download_url}
                                                    download
                                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                                >
                                                    <Download className="w-5 h-5" />
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h4 className="font-semibold text-gray-700">Update Status</h4>
                                <div className="mt-2 flex items-center space-x-2">
                                    <select
                                        value={selectedOrder.status}
                                        onChange={(e) => updateOrderStatus(selectedOrder.order_id, e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                        <option value="processing">Processing</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}